import {customElement, property} from "lit/decorators.js";
import {css, html, LitElement, nothing, PropertyValues} from "lit";
import {Interest, InterestIndex, InterestSelectionState} from "../interest-index";
import {SignalWatcher} from "@lit-labs/signals";
import tippy, {createSingleton} from "tippy.js";
import * as PushSubscriptions from "../push-subscriptions";

declare global {
    interface HTMLElementTagNameMap {
        'interest-toggles': InterestToggles;
    }
}

enum InterestType {
    ShowEventsInCategory = "ShowEventsInCategory",
    ShowPostsForEvent = "ShowPostsForEvent",
    ShowPostsInCategory = "ShowPostsInCategory",
    EmailWhenOrderingStartedInCategory = "EmailWhenOrderingStartedInCategory",
    EmailWhenPostPublishedForEvent = "EmailWhenPostPublishedForEvent",
    EmailWhenPostPublishedInCategory = "EmailWhenPostPublishedInCategory",
    PushWhenOrderingStartedInCategory = "PushWhenOrderingStartedInCategory",
    PushWhenPostPublishedForEvent = "PushWhenPostPublishedForEvent",
    PushWhenPostPublishedInCategory = "PushWhenPostPublishedInCategory",
}

interface InterestDescription {
    type: InterestType,
    icon: string,
    description: string
}

interface InterestDescriptionGroup {
    icon: string
    interests: InterestDescription[]
}

@customElement('interest-toggles')
export class InterestToggles extends SignalWatcher(LitElement) {
    static styles = css`
        .warning {
            font-family: var(--md-sys-typescale-body-medium-font);
            font-size: var(--md-sys-typescale-body-medium-size);
            line-height: var(--md-sys-typescale-body-medium-line-height);
            font-weight: var(--md-sys-typescale-body-medium-weight);
            letter-spacing: var(--md-sys-typescale-body-medium-tracking);
            
            padding: 16px;
            border: 1px solid var(--md-sys-color-outline-variant);
            border-radius: 16px;
        }
    `;
    
    static interestGroups: InterestDescriptionGroup[] = [
        // {
        //     icon: 'home',
        //     interests: [
        //         {
        //             type: InterestType.ShowPostsInCategory,
        //             icon: 'chat',
        //             description: "Posztok megjelenítése a főoldalon"
        //         },
        //         {
        //             type: InterestType.ShowEventsInCategory,
        //             icon: 'event',
        //             description: "Események megjelenítése a főoldalon"
        //         },
        //     ],
        // },
        {
            icon: 'chat_bubble',
            interests: [
                {
                    type: InterestType.PushWhenPostPublishedInCategory,
                    icon: 'mobile_chat',
                    description: "Értesítés új posztokról"
                },
                {
                    type: InterestType.EmailWhenPostPublishedInCategory,
                    icon: 'mail',
                    description: "Email új posztokról"
                },
            ],
        },
        {
            icon: 'room_service',
            interests: [
                {
                    type: InterestType.PushWhenOrderingStartedInCategory,
                    icon: 'mobile_chat',
                    description: "Értesítés rendelés kezdetekor"
                },
                {
                    type: InterestType.EmailWhenOrderingStartedInCategory,
                    icon: 'mail',
                    description: "Email rendelés kezdetekor"
                },
            ],
        },
    ];

    @property({type: Number}) category: number = 0;

    async handleToggled(e: Event) {
        const button = e.target as (EventTarget & { interestId: number });
        const interestId = button.interestId;
        const interest = InterestIndex.interests.get(interestId)!;
        let selected = InterestIndex.subscriptions.has(interestId);
        selected = !selected;

        if (selected) {
            const registerDeviceForPush =
                interest.name.includes('Push')
                && !PushSubscriptions.hasRegisteredDevices.get()
                && !PushSubscriptions.noPushOnThisDevice.get()
                && !PushSubscriptions.pushInterestsFollowed.get()
                && PushSubscriptions.permissionState.get() === "default";

            InterestIndex.subscriptions.add(interestId);
            const followInterest = fetch(`/api/interests/${interestId}/subscriptions`, {
                method: 'PUT',
            });

            if (registerDeviceForPush) {
                const registerPush = PushSubscriptions.registerDevice();
                await Promise.all([followInterest, registerPush]);
            } else
                await followInterest;
        } else {
            InterestIndex.subscriptions.delete(interestId);
            await fetch(`/api/interests/${interestId}/subscriptions`, {
                method: 'DELETE',
            });
        }
    }

    protected render() {
        const category = InterestIndex.categories.get(this.category);
        if (!category)
            return;
        const categoryInterests = category.interests;
        const loggedIn = window.isAuthenticated;

        return html`
            ${
                !loggedIn && html`
                    <p class="warning" style="display: flex; flex-direction: column; gap: 4px; margin: 16px 0">
                        Érdeklődési körök követéséhez jelentkezz be:
                        <login-and-return-button style="margin-top: 8px"></login-and-return-button>
                    </p>
                ` || nothing
            }
            
            <push-subscription-suggester></push-subscription-suggester>

            <div style="display: flex; flex-direction: column; gap: 8px">
                ${
                    InterestToggles.interestGroups.map(interestGroup => {
                        const interestsInGroup = interestGroup.interests
                            .map(interestType => {
                                const interest =
                                    categoryInterests.find(i => i.name == interestType.type);
                                if (!interest)
                                    return undefined;
                                return [
                                    interestType,
                                    interest,
                                ] as [description: InterestDescription, interest: Interest];
                            });
                        if (interestsInGroup.every(i => !i))
                            return null;
                        return html`
                            <div style="display: flex; gap: 2px; align-items: center">
                                <md-icon style="color: var(--md-sys-color-on-surface-variant)">
                                    ${interestGroup.icon}
                                </md-icon>
                                ${interestsInGroup.map((tuple) => {
                                    if (!tuple)
                                        return undefined;
                                    const [description, interest] = tuple;
                                    const icon = description.icon;
                                    const state = InterestIndex.getInterestSelectionState(interest).get();
                                    return html`
                                        <expressive-button
                                            ?soft-disabled="${!loggedIn}"
                                            class="extra-small ${
                                                state === InterestSelectionState.Selected
                                                    ? 'square filled'
                                                    : state === InterestSelectionState.IncluderSelected
                                                        ? 'round filled'
                                                        : 'round neutral'
                                            }"
                                            @click="${this.handleToggled}"
                                            .interestId="${interest.id}"
                                            .description="${description.description}">
                                            <md-icon>
                                                ${icon}
                                            </md-icon>
                                        </expressive-button>
                                    `;
                                })}
                            </div>
                        `;
                    })
                }
            </div>
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        createSingleton(
            tippy(
                this.renderRoot.querySelectorAll("expressive-button"),
                {
                    content(element) {
                        return (element as Element & { description: string }).description;
                    },
                },
            ),
        );
    }
}
